import React, { useLayoutEffect, useState } from 'react';
import axios from 'axios';
import { useAppDispatch } from 'hooks/redux';
import { findIndex, isEqual, sortBy, uniqBy } from 'lodash';
import { OnChangeValue, Theme } from 'react-select';
import AsyncSelect from 'react-select/async';
import { setElementValues } from 'store/rfiSlice';
import { answerValueTypes, elementTypes, ElementValue, IElementProps } from 'types/RfiTypes';

type OptionType = {
  value: string;
  label: string;
  disabled: boolean;
};

export const accessibleSelectTheme = {
  // components: {
  //   DropdownIndicator: SelectDropdownIndicator,
  //   ClearIndicator: SelectClearIndicator,
  //   // Input: SelectInput,
  // },
  theme: (theme: Theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      // unison-gray-70 accessible contrast on white background for "Select..." placeholder
      neutral50: "#736d6c",
      // disabled text color
      neutral40: "#000",
    },
  }),
};

const Lookup = (props: IElementProps) => {
  // Hooks
  const dispatch = useAppDispatch();

  // State
  const [lookupOptions, setLookupOptions] = useState<OptionType[]>(
    [] as OptionType[]
  );

  // const { name, value: checkedValues, onChange } = props;
  let defaultValue = props.element.answers
    ? props.element.answers[0]?.value
    : "";

  let defaultValues: string[] = [];
  props.element.answers?.map((answer: any) => {
    defaultValues.push(answer?.value);
  });

  const [value, setValue] = useState<string>(defaultValue ?? "");
  const [values, setValues] = useState<string[]>(defaultValues ?? [""]);

  // Functions
  const labelFormatter = (i: any): OptionType => {
    return {
      label: i.code + ": " + i.display,
      value: i.code,
      disabled: false,
    };
  };

  // add `return` keyword
  const loadOptions = async (
    inputValue: string,
    callback: any
  ): Promise<OptionType[]> => {
    let pageSize = 20;
    if (
      props.element.mapsToField === "RESPONSES_DUE_TZ" ||
      props.element.mapsToField === "SET_ASIDE"
    ) {
      pageSize = 100;
    }
    return axios
      .get(`/api/v1/lookup/${props.element.lookupCode}`, {
        params: { search: inputValue, page_size: pageSize, page_number: 0 },
      })
      .then((response) => {
        let data = response.data?.records;

        let resp: OptionType[] = data.map((result: any) => {
          return labelFormatter(result);
        });

        let selectedOptions: OptionType[] = [];
        props.element &&
          props.element.answers?.map((answer: any) => {
            axios
              .get(`/api/v1/lookup/${props.element.lookupCode}`, {
                params: { search: answer.value, page_size: 1, page_number: 0 },
              })
              .then((res) => {
                let data = res.data?.records;
                selectedOptions = data.map((result: any) => {
                  return labelFormatter(result);
                });
              })
              .finally(() => {
                selectedOptions = lookupOptions.concat(selectedOptions);
                let uniqueOptions = uniqBy(selectedOptions, "label");
                let sortedOptions = sortBy(
                  uniqueOptions,
                  (option: OptionType) => {
                    return option.label;
                  }
                );

                setLookupOptions(resp.concat(sortedOptions));
              });
          });

        return resp;
      });
  };

  // Effects
  useLayoutEffect(() => {
    //if (!props.readOnly) {
      let pageSize = 20;
      if (
        props.element.mapsToField === "RESPONSES_DUE_TZ" ||
        props.element.mapsToField === "SET_ASIDE"
      ) {
        pageSize = 100;
      }
      axios
        .get(`/api/v1/lookup/${props.element.lookupCode}`, {
          params: { search: "", page_size: pageSize, page_number: 0 },
        })
        .then((response) => {
          let data = response.data?.records;

          let resp = data.map((result: any) => {
            return labelFormatter(result);
          });

          let sortedResp = sortBy(resp, (option: OptionType) => {
            return option.label;
          });

          setLookupOptions(sortedResp);

          let selectedOptions: OptionType[] = [];
          props.element &&
            props.element.answers?.map((answer: any) => {
              axios
                .get(`/api/v1/lookup/${props.element.lookupCode}`, {
                  params: {
                    search: answer.value,
                    page_size: 1,
                    page_number: 0,
                  },
                })
                .then((res) => {
                  let data = res.data?.records;
                  selectedOptions = data.map((result: any) => {
                    return labelFormatter(result);
                  });
                })
                .finally(() => {
                  selectedOptions = resp.concat(selectedOptions);
                  let uniqueOptions = uniqBy(selectedOptions, "label");
                  let sortedOptions = sortBy(
                    uniqueOptions,
                    (option: OptionType) => {
                      return option.label;
                    }
                  );
                  setLookupOptions(sortedOptions);
                });
            });

          return resp;
          //setLookupOptions(resp);
        });
    //}
  }, [value]);

  // Handlers
  const handleChangeMulti = (
    e: OnChangeValue<OptionType | undefined, true>
  ) => {
    let selectedOptions = e as OptionType[];
    selectedOptions = selectedOptions.concat(lookupOptions);
    let uniqueOptions = uniqBy(selectedOptions, "label");
    setLookupOptions(uniqueOptions);

    let values: ElementValue[] = [];
    if (e !== null && typeof e.length !== "undefined") {
      e.forEach((answer: any) => {
        values.push({
          value: answer?.value,
          answerValueType: answerValueTypes.OPTION,
        });
      });
      let newValues: string[] = [];
      values?.map((answer: any) => {
        newValues.push(answer?.value);
      });
      setValues(newValues);
    }

    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: values,
      })
    );
  };

  const handleChangeSingle = (
    e: OnChangeValue<OptionType | undefined, false>
  ) => {
    if (e !== null) {
      let selectedOptions = [e] as OptionType[];
      selectedOptions = selectedOptions.concat(lookupOptions);
      let uniqueOptions = uniqBy(selectedOptions, "label");
      let sortedOptions = sortBy(uniqueOptions, (option: OptionType) => {
        return option.label;
      });
      setLookupOptions(sortedOptions);
    }

    let values: ElementValue[] = [];
    values.push({
      value: e?.value ?? "",
      answerValueType: answerValueTypes.DEFAULT,
    });
    setValues([e?.value ?? ""]);

    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: values,
      })
    );
  };

  // Render
  if (props.element.elementType === elementTypes.FIELD) {
    if (props.readOnly) {
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            {props.element.fieldLabel}
          </div>
          <div className="col-lg-8 fieldElement">
            {(
              lookupOptions &&
              lookupOptions.find((option) => option?.value === value)
            )?.label ?? value}
          </div>
        </div>
      );
    } else
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            <legend className="visuallyHidden">
              {props.element.fieldLabel}
            </legend>
            <label
              className="col-form-label"
              htmlFor={`lookup-${props.element.elementId}`}
            >
              <div>
                {props.element.fieldLabel}{" "}
                {props.overrideRequired === undefined ? (
                  props.element.requiredResponse ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )
                ) : props.overrideRequired === true ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )}
              </div>
            </label>
          </div>
          <div className="col-lg-8 fieldElement">
            {lookupOptions &&
              (props.isMulti ? (
                <AsyncSelect
                  onChange={handleChangeMulti}
                  inputId={`lookup-${props.element.elementId}`}
                  isMulti={true}
                  isClearable={true}
                  placeholder={<div>Type to search...</div>}
                  value={
                    values
                      ? values.map((item) => {
                          let index = findIndex(
                            lookupOptions,
                            (option: any) => {
                              return option?.value === item;
                            }
                          );
                          if (index >= 0) {
                            return lookupOptions[index];
                          }
                        })
                      : null
                  }
                  defaultOptions={lookupOptions}
                  loadOptions={loadOptions}
                  {...accessibleSelectTheme}
                />
              ) : (
                <AsyncSelect
                  onChange={handleChangeSingle}
                  inputId={`lookup-${props.element.elementId}`}
                  isMulti={false}
                  isClearable={true}
                  placeholder={<div>Type to search...</div>}
                  value={
                    values
                      ? values.map((item) => {
                          let index = findIndex(
                            lookupOptions,
                            (option: any) => {
                              return option?.value === item;
                            }
                          );
                          if (index >= 0) {
                            return lookupOptions[index];
                          }
                        })
                      : null
                  }
                  defaultOptions={lookupOptions}
                  loadOptions={loadOptions}
                  {...accessibleSelectTheme}
                />
              ))}
          </div>
        </div>
      );
  } else {
    if (props.readOnly) {
      return (
        <>
          <div className="col-form-label">
            {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
            {props.overrideRequired === undefined ? (
              props.element.requiredResponse ? (
                <span>*</span>
              ) : (
                ""
              )
            ) : props.overrideRequired === true ? (
              <span>*</span>
            ) : (
              ""
            )}
          </div>
          <div>
            <AsyncSelect
              {...accessibleSelectTheme}
              inputId={`lookup-${props.element.elementId}`}
              isMulti={props.isMulti}
              placeholder={<div>Type to search...</div>}
              value={null}
              isDisabled
            />
          </div>
        </>
      );
    } else
      return (
        <>
          <fieldset className="usa-fieldset-inputs usa-sans">
            <legend className="visuallyHidden">
              {props.element.fieldLabel}
            </legend>
            <label
              className="col-form-label"
              htmlFor={`lookup-${props.element.elementId}`}
            >
              <div>
                {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
                {props.overrideRequired === undefined ? (
                  props.element.requiredResponse ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )
                ) : props.overrideRequired === true ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )}
              </div>
            </label>
            <div>
              <AsyncSelect
                {...accessibleSelectTheme}
                inputId={`lookup-${props.element.elementId}`}
                isMulti={props.isMulti}
                isClearable={true}
                placeholder={<div>Type to search...</div>}
                value={
                  lookupOptions
                    ? lookupOptions.find((option) => option?.value === value)
                    : null
                }
                defaultOptions={lookupOptions}
                loadOptions={loadOptions}
                options={lookupOptions}
              />
            </div>
          </fieldset>
        </>
      );
  }
};

const areEqual = (prevProps: IElementProps, nextProps: IElementProps) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const LookupElement = React.memo(Lookup, areEqual);

export default LookupElement;
